// @ts-check

import { createContext, useContext } from "react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@radix-ui/react-collapsible"
import { TextShimmer } from "./ui/text-shimmer"
import { cn } from "../lib/utils"

const ToolCallState = /**@type{const}*/({
  PENDING: "pending",
  SUCCESS: "success",
  ERROR: "error"
})

const ToolCallContext = createContext({
  /** @type {typeof ToolCallState[keyof typeof ToolCallState]} */
  state: ToolCallState.PENDING,
})

/**
 * @param {React.ComponentProps<"div">} props
 */
function ToolCallHeader({ className, children, ...props }) {
  const { state } = useContext(ToolCallContext)
  const canExpand = state !== ToolCallState.PENDING
  
  return (
    <div 
      className={cn(
        "flex gap-2 items-center flex-nowrap px-3 py-2 rounded-lg max-w-full min-w-0 relative",
        className
      )}
      {...props}
    >
      {children}
      {canExpand && (
        <CollapsibleTrigger asChild>
          <button className="absolute inset-0 rounded-[inherit] bg-transparent hover:bg-muted/50 focus-visible:bg-muted/50 transition-colors">
            <span className="sr-only">Toggle</span>
          </button>
        </CollapsibleTrigger>
      )}
    </div>
  )
}

/**
 * @param {React.ComponentProps<"span"> & { children: string }} props
 */
function ToolCallAction({ className, children, ...props }) {
  const { state } = useContext(ToolCallContext)
  
  if (state === ToolCallState.PENDING) {
    return (
      <span className={cn("text-sm shrink-0", className)} {...props}>
        <TextShimmer 
          as="span" 
          gradientColors={['#666', '#fff']}
        >
          {children}
        </TextShimmer>
      </span>
    )
  }
  
  const stateColors = {
    [ToolCallState.SUCCESS]: "text-primary",
    [ToolCallState.ERROR]: "text-destructive"
  }
  
  return (
    <span 
      className={cn(
        "text-sm shrink-0",
        stateColors[state] ?? "text-foreground",
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
}

/**
 * @param {React.ComponentProps<"span">} props
 */
function ToolCallInput({ className, children, ...props }) {
  return (
    <span 
      className={cn(
        "text-sm text-foreground whitespace-nowrap overflow-hidden text-ellipsis",
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
}

/**
 * @param {React.ComponentProps<"div">} props
 */
function ToolCallContent({ className, children, ...props }) {
  const { state } = useContext(ToolCallContext)

  if (state === ToolCallState.PENDING) {
    return null
  }

  return (
    <CollapsibleContent className={cn("min-w-0", className)} {...props}>
      {children}
    </CollapsibleContent>
  )
}

/**
 * @param {React.ComponentProps<"div">} props
 */
function ToolCallOutput({ className, children, ...props }) {
  const { state } = useContext(ToolCallContext)

  return (
    <div
      className={cn(
        "grid gap-4 px-4 ml-4 border-l border-border overflow-x-auto min-w-0",
        state === ToolCallState.ERROR && "text-destructive/80",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

/**
 * @typedef {Object} ToolCallExtraProps
 * @property {typeof ToolCallState[keyof typeof ToolCallState]} state
 */

/**
 * @param {React.ComponentProps<"div"> & ToolCallExtraProps} props
 */
function ToolCall({ state, className, children, ...props }) {
  return (
    <ToolCallContext value={{state}}>
      <Collapsible>
        <div 
          className={cn(
            "grid gap-2",
            className
          )}
          {...props}
        >
          {children}
        </div>
      </Collapsible>
    </ToolCallContext>
  )
}

ToolCall.Header = ToolCallHeader
ToolCall.Action = ToolCallAction
ToolCall.Input = ToolCallInput
ToolCall.Content = ToolCallContent
ToolCall.Output = ToolCallOutput
ToolCall.State = ToolCallState

export default ToolCall
export {
  ToolCallState
}